import { useEffect, useState } from "react";
import api from "@/lib/api";
import { money, fmtDate } from "@/lib/format";
import { Loader2, BookOpen, FileDown, FileSpreadsheet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import DateRangePicker, { rangeQuery, rangeReady } from "@/components/DateRangePicker";

const PERIODS = [{ k: "today", l: "Aujourd'hui" }, { k: "week", l: "Semaine" }, { k: "month", l: "Mois" }, { k: "year", l: "Année" }, { k: "all", l: "Tout" }, { k: "custom", l: "Personnalisée" }];

export default function Comptabilite() {
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [range, setRange] = useState({ from: "", to: "" });
  const ready = rangeReady(period, range);

  const download = async (fmt) => {
    setBusy(fmt);
    try {
      const res = await api.get(`/finance/accounting/${fmt}?${rangeQuery(period, range)}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `clinit_journal_comptable_${period === "custom" ? `${range.from}_${range.to}` : period}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(`Échec du téléchargement (${fmt === "pdf" ? "PDF" : "Excel"})`);
    } finally {
      setBusy("");
    }
  };

  useEffect(() => {
    if (!ready) { return; }
    setLoading(true);
    api.get(`/finance/accounting?${rangeQuery(period, range)}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [period, range, ready]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div><h1 className="text-2xl sm:text-3xl font-bold font-heading">Comptabilité</h1><p className="text-slate-500 text-sm mt-1">Journal des produits et charges</p></div>
        <div className="flex flex-col items-stretch sm:items-end gap-2">
          <div className="flex flex-wrap gap-1 bg-white border rounded-xl p-1">
            {PERIODS.map((p) => <button key={p.k} data-testid={`compta-period-${p.k}`} onClick={() => setPeriod(p.k)} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${period === p.k ? "bg-brand-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}>{p.l}</button>)}
          </div>
          {period === "custom" && <DateRangePicker testid="compta-range" value={range} onChange={setRange} />}
        </div>
      </div>
      {period === "custom" && !ready && (
        <p data-testid="compta-range-hint" className="text-sm text-slate-500">Choisissez une date de début et une date de fin pour afficher le journal.</p>
      )}

      {!ready ? null : loading || !data ? <div className="h-40 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-brand-600" /></div> : (
        <>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white border rounded-2xl p-5"><div className="text-sm text-slate-500">Produits</div><div data-testid="compta-produits" className="text-2xl font-bold font-heading text-emerald-600 mt-1">{money(data.produits)}</div></div>
            <div className="bg-white border rounded-2xl p-5"><div className="text-sm text-slate-500">Charges</div><div data-testid="compta-charges" className="text-2xl font-bold font-heading text-red-600 mt-1">{money(data.charges)}</div></div>
            <div className="bg-white border rounded-2xl p-5"><div className="text-sm text-slate-500">Résultat</div><div data-testid="compta-resultat" className={`text-2xl font-bold font-heading mt-1 ${data.resultat >= 0 ? "text-brand-700" : "text-red-600"}`}>{money(data.resultat)}</div></div>
          </div>

          <div className="bg-white border rounded-2xl overflow-hidden">
            <div className="px-4 py-3 border-b flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 font-semibold"><BookOpen className="w-4 h-4 text-brand-600" /> Journal</div>
              <div className="flex flex-wrap gap-2 justify-end">
                <Button data-testid="compta-download-xlsx" size="sm" onClick={() => download("xlsx")} disabled={!!busy} className="bg-emerald-600 hover:bg-emerald-700">
                  {busy === "xlsx" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-1.5" />} Excel
                </Button>
                <Button data-testid="compta-download-pdf" size="sm" onClick={() => download("pdf")} disabled={!!busy} className="bg-red-600 hover:bg-red-700">
                  {busy === "pdf" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileDown className="w-4 h-4 mr-1.5" />} PDF
                </Button>
              </div>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr><th className="text-left px-4 py-2">Date</th><th className="text-left px-4 py-2">Libellé</th><th className="text-left px-4 py-2">Type</th><th className="text-right px-4 py-2">Montant</th></tr>
              </thead>
              <tbody>
                {data.entries.map((e, i) => (
                  <tr key={`${e.type}-${e.date}-${e.amount}-${i}`} className="border-t">
                    <td className="px-4 py-2 text-slate-500">{fmtDate(e.date)}</td>
                    <td className="px-4 py-2">{e.label}</td>
                    <td className="px-4 py-2"><span className={`text-xs rounded-full px-2 py-0.5 ${e.type === "produit" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>{e.type === "produit" ? "Produit" : "Charge"}</span></td>
                    <td className={`px-4 py-2 text-right font-semibold ${e.type === "produit" ? "text-emerald-600" : "text-red-600"}`}>{e.type === "produit" ? "+" : "-"}{money(e.amount)}</td>
                  </tr>
                ))}
                {data.entries.length === 0 && <tr><td colSpan="4" className="text-center py-10 text-slate-400">Aucune écriture</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
